import { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import apiClient from "../utils/apiClient";
import RecipeSummaryCardCalendar from "./RecipeSummaryCardCalendar";

export default function RecipeSearchModal({
  isOpen,
  onClose,
  onSelectRecipe,
  selectedDate,
  existingEntries = []
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [onlyFavourites, setOnlyFavourites] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);

  const LIMIT = 20;

  const fetchRecipes = useCallback(async (pageToFetch = 1) => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const params = {
        q: searchTerm,
        filter: onlyFavourites ? "favourites" : "all",
        page: pageToFetch,
        limit: LIMIT,
      };
      const res = await apiClient.get("/recipes/search", { params });
      const { data, hasNextPage: next, page: current } = res.data;

      if (pageToFetch === 1) {
        setRecipes(data);
      } else {
        setRecipes(prev => [...prev, ...data]);
      }

      setHasNextPage(next);
      setPage(current);
    } catch (err) {
      console.error("Failed to fetch recipes", err);
    } finally {
      setLoading(false);
    }
  }, [isOpen, onlyFavourites, searchTerm]);

  // Reset & fetch on open or filter/search change
  useEffect(() => {
    if (isOpen) {
      setRecipes([]); // 💥 clear old data to prevent duplicates
      fetchRecipes(1);
    }
  }, [isOpen, onlyFavourites, searchTerm, fetchRecipes]);

  const handleLoadMore = () => {
    if (!loading && hasNextPage) {
      fetchRecipes(page + 1);
    }
  };

  if (!isOpen) return null;

  const existingRecipeIds = new Set(existingEntries.map((e) => e.recipeId._id));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-center items-center px-4">
      <div className="bg-white w-full max-w-md p-6 rounded shadow relative">
        <h2 className="text-lg font-semibold mb-4">
          Add recipe to{" "}
          <span className="text-sage">
            {format(new Date(selectedDate), "EEEE d MMM")}
          </span>
        </h2>

        {/* Search input */}
        <input
          type="text"
          placeholder="Search recipes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full border px-3 py-2 rounded text-sm mb-3"
        />

        {/* Toggle Buttons */}
        <div className="flex mb-4 text-sm rounded overflow-hidden border">
          <button
            className={`flex-1 py-2 font-medium ${
              !onlyFavourites ? "bg-sage text-white" : "bg-white text-sage"
            }`}
            onClick={() => setOnlyFavourites(false)}
          >
            All Recipes
          </button>
          <button
            className={`flex-1 py-2 font-medium ${
              onlyFavourites ? "bg-sage text-white" : "bg-white text-sage"
            }`}
            onClick={() => setOnlyFavourites(true)}
          >
            Favourites
          </button>
        </div>

        {/* Recipe List */}
        <div className="max-h-60 overflow-y-auto space-y-2">
          {recipes.map((recipe) => {
            const isAlreadyAdded = existingRecipeIds.has(recipe._id);
            return (
              <button
                key={recipe._id}
                onClick={() => onSelectRecipe(recipe)}
                disabled={isAlreadyAdded}
                className={`block w-full text-left transition ${
                  isAlreadyAdded
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "hover:bg-sage-50"
                }`}
              >
                <RecipeSummaryCardCalendar
                  recipe={recipe}
                  disabled={isAlreadyAdded}
                />
              </button>
            );
          })}
          {!loading && recipes.length === 0 && (
            <p className="text-sm text-gray-500 text-center">No matching recipes</p>
          )}
          {hasNextPage && (
            <div className="text-center mt-3">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="text-sage text-sm hover:underline disabled:opacity-50"
              >
                {loading ? "Loading..." : "Load more"}
              </button>
            </div>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-3 text-xl text-gray-500 hover:text-black"
          aria-label="Close"
        >
          &times;
        </button>
      </div>
    </div>
  );
}
